const isXmlWhitespace = (code: number): boolean => code === 9 || code === 10 || code === 13 || code === 32;

function getXmlEncoding(bytes: Uint8Array): string {
  if ((bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[1] === 0 && (bytes[0] === 60 || isXmlWhitespace(bytes[0])))) return 'utf-16le';
  if ((bytes[0] === 0xfe && bytes[1] === 0xff) || (bytes[0] === 0 && (bytes[1] === 60 || isXmlWhitespace(bytes[1])))) return 'utf-16be';
  return 'utf-8';
}

export function looksLikeXmlBytes(bytes: Uint8Array): boolean {
  const encoding = getXmlEncoding(bytes);
  let start = encoding === 'utf-8'
    ? (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0)
    : (bytes[0] === 0xff || bytes[0] === 0xfe ? 2 : 0);
  const step = encoding === 'utf-8' ? 1 : 2;
  const code = (index: number): number => encoding === 'utf-8' ? bytes[index]
    : encoding === 'utf-16le' ? bytes[index] | (bytes[index + 1] << 8)
    : (bytes[index] << 8) | bytes[index + 1];
  while (start < bytes.length && isXmlWhitespace(code(start))) start += step;
  return start < bytes.length && code(start) === 60;
}

/** Decode uploaded XML without DOM APIs so workers and previews agree. */
export function decodeSvgBytes(bytes: Uint8Array): string | undefined {
  const encoding = getXmlEncoding(bytes);

  try {
    const text = new TextDecoder(encoding, { fatal: true }).decode(bytes);
    return findSvgRoot(text) ? text : undefined;
  } catch {
    return undefined;
  }
}

export class SvgAssetRenderError extends Error {
  constructor() {
    super('SVG artwork could not be rendered.');
    this.name = 'SvgAssetRenderError';
  }
}

function findSvgRoot(text: string): string | undefined {
    let root = text.replace(/^\uFEFF/, '').trimStart();
    while (root.startsWith('<?') || root.startsWith('<!--') || root.startsWith('<!DOCTYPE')) {
      if (root.startsWith('<?') || root.startsWith('<!--')) {
        const terminator = root.startsWith('<?') ? '?>' : '-->';
        const end = root.indexOf(terminator);
        if (end < 0) return undefined;
        root = root.slice(end + terminator.length).trimStart();
        continue;
      }
      // Scan the declaration without resolving entities. Internal subsets and
      // quoted external identifiers may contain '>'; resvg owns actual parsing.
      let quote = '';
      let depth = 0;
      let end = -1;
      for (let i = 9; i < root.length; i++) {
        const character = root[i];
        if (quote) { if (character === quote) quote = ''; }
        else if (character === '"' || character === "'") quote = character;
        else if (root.startsWith('<!--', i)) {
          const commentEnd = root.indexOf('-->', i + 4);
          if (commentEnd < 0) return undefined;
          i = commentEnd + 2;
        } else if (character === '[') depth++;
        else if (character === ']') depth--;
        else if (character === '>' && depth === 0) { end = i; break; }
      }
      if (end < 0) return undefined;
      root = root.slice(end + 1).trimStart();
    }
    // This selects the decoder, not upload validation. resvg parses the XML,
    // including namespace bindings; the backend validates the SVG namespace.
    return /^<(?:[^\s<>/:]+:)?svg(?:\s|\/?>)/.test(root) ? root : undefined;
}

/** Render the original image document through a bounded stretch or cover viewport. */
export function resizeSvgViewport(text: string, width: number, height: number, intrinsicWidth: number, intrinsicHeight: number, fit: 'fill' | 'cover' = 'fill', coverViewport?: { width: number; height: number }): string {
  // A separate SVG image document preserves its original viewport/viewBox,
  // CSS selector context and XML prolog. Quantize only the outer raster axes.
  const source = encodeURIComponent(text);
  if (fit === 'cover') {
    // Crop against the logical field before mapping into the rounded bitmap.
    const viewport = coverViewport ?? { width, height };
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${viewport.width} ${viewport.height}" preserveAspectRatio="none"><image width="${viewport.width}" height="${viewport.height}" preserveAspectRatio="xMidYMid slice" href="data:image/svg+xml,${source}"/></svg>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${intrinsicWidth} ${intrinsicHeight}" preserveAspectRatio="none"><image width="${intrinsicWidth}" height="${intrinsicHeight}" href="data:image/svg+xml,${source}"/></svg>`;
}
